import React, { useContext, useState, useEffect } from 'react';
import styled from 'styled-components';
import { TrackClickAfterSearch } from '@repo/utils/algoliaAnalytics';
import { CartContext } from '@repo/contexts/CartContext';
import { DealerSearchContext } from '@repo/contexts/DealerSearchContext';
import { CustomerContext } from '@repo/contexts/CustomerContext';
import { LocaleContext } from '@repo/contexts/LocaleContext';
import getPercentageDiscountPrice from '@repo/utils/getPercentageDiscountPrice';
import generateProductSlug from '@repo/utils/generateProductSlug';
import hasCompanyOwnedDealerVariants from '@repo/utils/hasCompanyOwnedDealerVariants';
import canApplyProStaffDiscount from '@repo/utils/canApplyProStaffDiscount';
import {
  findMinMaxVariantPriceCad,
  findCompareAtPriceRangeCad,
} from '@repo/utils/canadaStore';
import { LocalizedLink } from '@repo/gatsby-localization-helpers';
import { formatReviewSku } from '@repo/utils';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import { ProductType } from '@repo/models/product';

import breakpoints from '../breakpoints';

interface SanityProduct {
  store?: {
    variants?: {
      canadaStore: { compareAtPrice: number; price: number };
    }[];
  };
}

interface ProductCardProps {
  product: ProductType;
  sanityProduct?: SanityProduct;
  showFeatureList?: boolean;
  showRatings?: boolean;
  showInlineRatings?: boolean;
  hideProductPrices?: boolean;
  productTitleAs?: string;
  categorySource?: string;
}

const Product = styled.div`
  width: 100%;
  height: 100%;
  text-align: left;
  padding: 0.625rem;
  border: 1px solid ${({ theme }) => theme.productCard.borderColor};
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.productCard.backgroundColor};
  box-shadow: ${({ theme }) => theme.productCard.boxShadow};

  &:hover {
    border: 1px solid ${({ theme }) => theme.productCard.borderColorHover};
    box-shadow: ${({ theme }) => theme.productCard.boxShadowHover};
  }

  @media (min-width: ${breakpoints.md}) {
    padding: 1rem;
  }

  .main-image-link {
    flex-grow: 0;
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  margin-bottom: 1rem;
  object-fit: contain;
  object-position: top center;
  aspect-ratio: 1;
`;

const ProductRatings = styled.div`
  margin-bottom: 1rem;
`;

const InlineProductRatingsContainer = styled.div`
  margin: 0 0 ${({ theme }) => theme.productCard.inlineRatingsMarginBottom};
  text-align: revert;
  width: fit-content;
`;

const ProductTitle = styled<any>(LocalizedLink)`
  flex-grow: 0;
  text-decoration: none;
  color: ${({ theme }) => theme.productCard.titleColor};

  &:hover {
    color: ${({ theme }) => theme.productCard.titleColorHover};
  }

  .product-title,
  h2 {
    margin: 0px 0px 0.25em;
    font-size: 1rem;
    @media (min-width: ${breakpoints.xs}) {
      font-size: clamp(1rem, 4vw, 1.1875rem);
    }

    text-transform: ${({ theme }) => theme.productCard.titleTextTransform};
    font-weight: ${({ theme }) => theme.productCard.titleFontWeight};
    color: inherit;
  }
`;

const ProductFeatures = styled.ul`
  list-style: revert;
  list-style-position: outside;
  text-align: left;
  margin-bottom: 0;
  padding: 0;
  padding-left: 1rem;
  flex-grow: 1;

  li {
    margin-bottom: 0.25rem;
  }
`;

const ProductPrice = styled.div`
  font-size: 1.1875rem;
  font-weight: revert;
  font-weight: bolder;
  margin-top: 0.625rem;
  color: ${({ theme }) => theme.productCard.priceColor};

  .pre-discount-price {
    color: grey;
    font-weight: 400;
    margin-right: 0.2rem;
    text-decoration: line-through;
  }

  s {
    font-size: 1rem;
  }
`;

const SalePrice = styled.span`
  color: ${({ theme }) => theme.colors.onSaleRed};
`;

function filterProductForCompanyOwnedDealerVariants(product, filterValue) {
  // Create a deep copy of the product to avoid modifying the original object
  const filteredProduct = JSON.parse(JSON.stringify(product));

  // Filter the variants
  filteredProduct.variants = filteredProduct.variants.filter(variant =>
    variant.metafields.some(
      metafield =>
        metafield.key === 'company_owned_dealer_variant' &&
        metafield.value === filterValue.toString()
    )
  );

  // Recalculate the price range and set the lowest price
  if (filteredProduct.variants.length > 0) {
    let minPrice = filteredProduct.variants[0]?.price;
    let maxPrice = filteredProduct.variants[0]?.price;

    filteredProduct.variants.forEach(variant => {
      if (variant.price < minPrice) minPrice = variant.price;
      if (variant.price > maxPrice) maxPrice = variant.price;
    });

    filteredProduct.priceRangeV2 = {
      minVariantPrice: {
        amount: minPrice,
      },
      maxVariantPrice: {
        amount: maxPrice,
      },
    };

    // Set the product price to the lowest price from the filtered variants
    filteredProduct.price = minPrice;
  } else {
    // If no variants are found, set price range and price to null or default values
    filteredProduct.priceRangeV2 = {
      minVariantPrice: {
        amount: null,
      },
      maxVariantPrice: {
        amount: null,
      },
    };
    filteredProduct.price = null;
  }
  return filteredProduct;
}

const findCompareAtPriceRange = product => {
  const { minVariantPrice, maxVariantPrice } = product.priceRangeV2 ?? {};

  let min = product.variants[0]?.compareAtPrice;
  let max = product.variants[0]?.compareAtPrice;

  if (minVariantPrice?.amount < maxVariantPrice?.amount && min > 0) {
    product.variants.forEach(variant => {
      if (min > variant.compareAtPrice) {
        min = variant.compareAtPrice;
      }
      if (max < variant.compareAtPrice) {
        max = variant.compareAtPrice;
      }
    });
  }

  return {
    minVariantPrice: {
      amount: min,
      currencyCode: 'USD',
    },
    maxVariantPrice: {
      amount: max,
      currencyCode: 'USD',
    },
  };
};

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  sanityProduct,
  showFeatureList,
  showRatings,
  showInlineRatings,
  hideProductPrices,
  productTitleAs = 'h2',
  categorySource = '',
}) => {
  const { tt } = useUiComponentsInternationalContext();

  const localeContext = useContext(LocaleContext);
  const currency = localeContext?.currency || 'USD';
  const [isBrowser, setIsBrowser] = useState(false);
  const { title, productType, handle, image, sku } = product;
  const slugLocalePrefix = currency === 'CAD' ? 'en-ca/' : '';

  const dealerContext = useContext(DealerSearchContext);

  let slug = `${slugLocalePrefix}${generateProductSlug({
    productHandle: handle,
    productType,
  })}`;

  const ProductTitleComp: React.ElementType = productTitleAs;

  // if slug has product/product in it, remove one of them
  if (slug.includes('product/product')) {
    const slugArray = slug.split('/');
    slugArray.splice(1, 1);
    slug = slugArray.join('/');
    // if slug has p/ append sku to the end
  } else if (slug.includes('p/')) {
    if (slug.includes('p/p')) {
      const slugArray = slug.split('/');
      slugArray.splice(1, 1);
      slug = slugArray.join('/');
    }
    // Split the slug and check the structure after 'p'
    const slugParts = slug.split('/');
    const pIndex = slugParts.indexOf('p');

    if (pIndex !== -1 && slugParts.length === pIndex + 2) {
      // Format is p/something, so we can append the SKU
      slug = `${slug}/${sku}`;
    }
  }

  const context = useContext(CustomerContext);
  const { formatPrice } = useContext(CartContext);

  const proStaffDiscount = process.env.GATSBY_PRO_STAFF_DISCOUNT_PERCENTAGE;

  const productId = product?.shopifyId;

  useEffect(() => {
    if (typeof window !== `undefined`) {
      setIsBrowser(true);
    }
  }, []);

  const getCadDisplayPrice = (): string | JSX.Element => {
    const compareAtPriceRange = findCompareAtPriceRangeCad(sanityProduct);
    const priceRange = findMinMaxVariantPriceCad(sanityProduct);
    const firstVariantCompareAtPrice =
      sanityProduct?.store?.variants?.[0]?.canadaStore?.compareAtPrice;
    const firstVariantPrice =
      sanityProduct?.store?.variants?.[0]?.canadaStore?.price;

    // If there are different prices per variant and any variant has discount
    if (
      parseFloat(compareAtPriceRange?.minVariantPrice?.amount) <
        parseFloat(compareAtPriceRange?.maxVariantPrice?.amount) &&
      parseFloat(compareAtPriceRange?.minVariantPrice?.amount) >
        parseFloat(priceRange.minVariantPrice.amount)
    ) {
      return (
        <>
          <s>
            {formatPrice(compareAtPriceRange.minVariantPrice.amount)} -
            {formatPrice(compareAtPriceRange.maxVariantPrice.amount)}
          </s>
          <br />
          <SalePrice>
            {tt(s => s.productCommon.salePrice, {
              amount: `${formatPrice(
                priceRange.minVariantPrice.amount
              )} - ${formatPrice(priceRange.maxVariantPrice.amount)}`,
            })}
          </SalePrice>
        </>
      );
    }

    // If there are different prices per variant, but no discounts
    if (
      parseFloat(priceRange?.minVariantPrice?.amount) <
      parseFloat(priceRange?.maxVariantPrice?.amount)
    ) {
      // if user is pro staff apply pro staff discount to range
      if (
        context?.isProStaff() &&
        canApplyProStaffDiscount(product.tags, !firstVariantCompareAtPrice)
      ) {
        return `${formatPrice(
          getPercentageDiscountPrice({
            price: priceRange.minVariantPrice.amount,
            percentageDiscount: proStaffDiscount,
          })
        )} - ${formatPrice(
          getPercentageDiscountPrice({
            price: priceRange.maxVariantPrice.amount,
            percentageDiscount: proStaffDiscount,
          })
        )}`;
      }
      // return discounted price
      return `${formatPrice(priceRange.minVariantPrice.amount)} - ${formatPrice(
        priceRange.maxVariantPrice.amount
      )}`;
    }

    // All variants have the same price, but price is discounted
    if (product.variants && firstVariantCompareAtPrice > firstVariantPrice) {
      return (
        <>
          <s>{formatPrice(firstVariantCompareAtPrice)}</s>
          <br />
          <SalePrice>
            {tt(s => s.productCommon.salePrice, {
              amount: formatPrice(firstVariantPrice),
            })}
          </SalePrice>
        </>
      );
    }

    // Product price for first variant, no discounts
    return formatPrice(firstVariantPrice);
  };

  const getDisplayPrice = () => {
    let processedProduct = product;

    if (
      dealerContext?.storedDealer?.isCOS &&
      hasCompanyOwnedDealerVariants(product)
    ) {
      processedProduct = filterProductForCompanyOwnedDealerVariants(
        product,
        true
      );
    } else if (
      !dealerContext?.storedDealer?.isCOS &&
      hasCompanyOwnedDealerVariants(product)
    ) {
      processedProduct = filterProductForCompanyOwnedDealerVariants(
        product,
        false
      );
    }

    const compareAtPriceRange = findCompareAtPriceRange(processedProduct);

    // If there are different prices per variant and any variant has discount
    if (
      parseFloat(compareAtPriceRange?.minVariantPrice?.amount) <
        parseFloat(compareAtPriceRange?.maxVariantPrice?.amount) &&
      parseFloat(compareAtPriceRange?.minVariantPrice?.amount) >
        product.priceRangeV2?.minVariantPrice?.amount
    ) {
      return (
        <>
          <s>
            {formatPrice(compareAtPriceRange.minVariantPrice.amount)} -
            {formatPrice(compareAtPriceRange.maxVariantPrice.amount)}
          </s>
          <br />
          <SalePrice>
            {tt(s => s.productCommon.salePrice, {
              amount: `${formatPrice(
                processedProduct.priceRangeV2.minVariantPrice.amount
              )} - ${formatPrice(
                processedProduct.priceRangeV2.maxVariantPrice.amount
              )}`,
            })}
          </SalePrice>
        </>
      );
    }

    // If there are different prices per variant, but no discounts
    if (
      processedProduct?.priceRangeV2?.minVariantPrice?.amount <
      processedProduct?.priceRangeV2?.maxVariantPrice?.amount
    ) {
      // if user is pro staff apply pro staff discount to range
      if (
        context?.isProStaff() &&
        canApplyProStaffDiscount(
          processedProduct.tags,
          !processedProduct.variants[0]?.compareAtPrice
        )
      ) {
        return `${formatPrice(
          getPercentageDiscountPrice({
            price: processedProduct.priceRangeV2.minVariantPrice.amount,
            percentageDiscount: proStaffDiscount,
          })
        )} - ${formatPrice(
          getPercentageDiscountPrice({
            price: processedProduct.priceRangeV2.maxVariantPrice.amount,
            percentageDiscount: proStaffDiscount,
          })
        )}`;
      }
      // return discounted price
      return `${formatPrice(
        processedProduct.priceRangeV2.minVariantPrice.amount
      )} - ${formatPrice(
        processedProduct.priceRangeV2.maxVariantPrice.amount
      )}`;
    }

    // All variants have the same price, but price is discounted
    if (
      processedProduct.variants &&
      processedProduct.variants[0]?.compareAtPrice >
        processedProduct.variants[0]?.price
    ) {
      return (
        <>
          <s>{formatPrice(processedProduct.variants[0]?.compareAtPrice)}</s>
          <br />
          <SalePrice>
            {tt(s => s.productCommon.salePrice, {
              amount: formatPrice(processedProduct.variants[0]?.price),
            })}
          </SalePrice>
        </>
      );
    }

    // Provided price value with product
    if (processedProduct.price) {
      if (
        context?.isProStaff() &&
        canApplyProStaffDiscount(
          processedProduct.tags,
          !processedProduct.variants[0]?.price
        )
      ) {
        return (
          <>
            <span className="pre-discount-price">
              {formatPrice(processedProduct.price)}
            </span>
            {formatPrice(
              getPercentageDiscountPrice({
                price: processedProduct.price,
                percentageDiscount:
                  process.env.GATSBY_PRO_STAFF_DISCOUNT_PERCENTAGE,
              })
            )}
          </>
        );
      }
      return `${formatPrice(processedProduct.price)}`;
    }

    // Product price for first variant, no discounts
    return `${formatPrice(processedProduct.variants[0]?.price)}`;
  };

  const getProductLifecycle = lifecycle => {
    if (lifecycle === 'Obsolete') return 'Discontinued';
    return 'Coming Soon';
  };

  return (
    <Product>
      <LocalizedLink
        to={`/${slug}`}
        className="main-image-link"
        onClick={() => {
          if (typeof product.insights === 'function') {
            TrackClickAfterSearch(product?.insights);
          }
        }}
        state={{
          searchedProduct: product.queryID || null,
          categorySource,
        }}
        tabIndex={-1}
        aria-hidden="true"
      >
        <ProductImage
          src={image.src}
          alt={image.altText}
          width={350}
          height={350}
        />
      </LocalizedLink>
      {isBrowser && showInlineRatings && (
        <InlineProductRatingsContainer
          className="ratings-container"
          id={`BVRRInlineRating-${productId}`}
          data-bazaarvoice-id={productId}
          data-bazaarvoice-slug={slug}
        />
      )}
      {isBrowser && showRatings && (
        <ProductRatings
          data-bv-show="inline_rating"
          data-bv-product-id={formatReviewSku(sku)}
          data-bv-seo="false"
          data-bv-redirect-url={`/${slug}`}
        />
      )}
      <ProductTitle
        to={`/${slug}`}
        onClick={() => {
          if (typeof product.insights === 'function') {
            TrackClickAfterSearch(product?.insights);
          }
        }}
        state={{
          searchedProduct: product.queryID || null,
          categorySource,
        }}
      >
        <ProductTitleComp className="product-title">{title}</ProductTitleComp>
      </ProductTitle>
      {showFeatureList && product?.features && (
        <ProductFeatures>
          {product.features.map(feature => (
            <li key={feature}>{feature}</li>
          ))}
        </ProductFeatures>
      )}

      {!hideProductPrices &&
      product?.productLifecycle?.value !== 'Coming Soon' &&
      product?.productLifecycle?.value !== 'Obsolete' ? (
        <ProductPrice>
          {currency === 'CAD' ? getCadDisplayPrice() : getDisplayPrice()}
        </ProductPrice>
      ) : (
        <ProductPrice>
          {getProductLifecycle(product?.productLifecycle?.value)}
        </ProductPrice>
      )}
    </Product>
  );
};

export default ProductCard;
